import * as Sentry from "@sentry/react";

// Resolve Sentry environment tag.
// Precedence: explicit VITE_SENTRY_ENVIRONMENT (baked in at build time per deploy —
// production / staging / ephemeral-N) → Vite MODE → 'development'. Staging and prod
// both build in production mode, so the explicit build arg is required to separate
// their alert channels (#135).
const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT
  || import.meta.env.MODE
  || 'development';

// Match backend sampling strategy: low sample in prod/staging, full in dev/ephemeral.
const tracesSampleRate = (sentryEnvironment === 'production' || sentryEnvironment === 'staging')
  ? 0.1
  : 1.0;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: sentryEnvironment,
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  ignoreErrors: [
    /Failed to read a named property.*from 'Window'/,
    // External email-scanner noise (Outlook SafeLink). The /for/digcomp route is a
    // static public page with no update() method; this signature is injected by
    // Outlook's WebView scanner when it preview-loads emailed URLs. 1 event ever,
    // 0 users affected (2026-07-28). #1909
    /Object Not Found Matching Id/,
  ],
  // Issue #981 review-fix: drop expected superseded-session 400s. The
  // testing-panel session model legitimately produces these when a tester
  // has multiple tabs open; api.js tags such errors with
  // `expectedSupersededSession = true`. Without this hook, the marker has
  // no consumer and the error still reaches Sentry (codex + review-code
  // consensus pre-deploy).
  beforeSend(event, hint) {
    const err = hint?.originalException;
    if (err && typeof err === 'object' && err.expectedSupersededSession) {
      return null;
    }
    return event;
  },
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,  // Privacy - mask user input
      blockAllMedia: true,
    }),
  ],
  tracesSampleRate,
  replaysSessionSampleRate: 0,     // Only capture replays on error
  // Always replay on error, except under automation: Playwright's headless
  // Chrome on staging produced 25 of the 44 replays in the first week of the
  // 2026-09-22 billing period, against a 50/month plan quota. navigator.webdriver
  // is true under Playwright/Selenium; errors are still reported either way.
  replaysOnErrorSampleRate: (typeof navigator !== 'undefined' && navigator.webdriver) ? 0 : 1.0,
});

export default Sentry;
